import { useSyncExternalStore } from 'react';
import type { AgentActivityState, WsServerMessage } from '@reflex/shared';
import { getSocketManager } from '@/lib/socketManager';

/**
 * Ephemeral per-agent "working" vs "idle" signal delivered over the
 * WebSocket via `agent:activity` messages. Agent rows stay at
 * `status: 'running'` across multiple turns (the devbox survives
 * follow-up messages), so status alone can't distinguish a turn in
 * flight from an idle devbox waiting for input. Display surfaces read the
 * durable `agent.turnState` for that (`activityFromDurableAgentState`);
 * this live map survives as one of the two evidence sources
 * `unseenActivityFor` accepts for unseen/unread semantics — see
 * `docs/architecture/agent-status-propagation.md`.
 *
 * The store subscribes to the socket eagerly at module load time
 * and fans out to React consumers via `useSyncExternalStore`. Every
 * list row reads from the same snapshot, so a turn boundary triggers
 * one re-render per row, not N socket handlers.
 */

export type Snapshot = ReadonlyMap<string, AgentActivityState>;

type Listener = () => void;

const listeners = new Set<Listener>();
let snapshot: Snapshot = new Map();

function setActivity(agentId: string, state: AgentActivityState): void {
  const next = new Map(snapshot);
  next.set(agentId, state);
  publish(next);
}

/**
 * Adopt the server's complete activity table, sent once per (re)connect.
 *
 * A replace, not a merge. This map is module-global and outlives socket
 * drops, but the server's is in-memory and per-replica: after a restart —
 * or on a socket that reconnects onto a replica that never saw the turn —
 * it simply has no snapshot for an agent. Merging would keep our stale
 * `idle`, and `unseenActivityFor` treats a live `idle` as finished-turn
 * evidence, so the row would carry an unseen dot for the whole of a live
 * turn. Dropping the entry falls back to no-evidence → `working`, which
 * is exactly what a fresh page load shows.
 */
function replaceActivities(
  entries: readonly { agentId: string; state: AgentActivityState }[],
): void {
  publish(new Map(entries.map((entry) => [entry.agentId, entry.state])));
}

function publish(next: Snapshot): void {
  snapshot = next;
  for (const listener of listeners) listener();
}

// Register the WS handler eagerly so we never miss replay messages that
// arrive before any React component subscribes via useSyncExternalStore.
// Previously this was lazy (ensureSocketListener called on first subscribe),
// which meant activity replays were dropped if the WS connected before the
// agent list view mounted — e.g. when the user navigated to the detail view
// first. ACP agents were affected because they stay status: 'running' and
// rely entirely on this ephemeral WS signal (Claude agents bypass it via
// a durable status change to 'completed').
const manager = getSocketManager();
manager.onMessage((msg: WsServerMessage) => {
  if (msg.type === 'agent:activity_snapshot') {
    replaceActivities(msg.activities);
    return;
  }
  if (msg.type !== 'agent:activity') return;
  setActivity(msg.agentId, msg.state);
});

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): Snapshot {
  return snapshot;
}

/**
 * Subscribe to the shared activity map. Re-renders the caller whenever
 * any agent's `working`/`idle` state flips.
 */
export function useLiveAgentTurnSignals(): Snapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Test-only helper: reset the in-memory snapshot between tests. Does not
 * detach the socket listener (tests that need a clean slate should
 * reset the mocked `SocketManager` too).
 */
export function _resetAgentTurnSignalsForTest(): void {
  publish(new Map());
}
